import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IS_FIREBASE_CONFIGURED } from '../../config/firebase';
import {
  ChevronLeft,
  ChevronDown,
  Plus,
  Pencil,
  Trash2,
  Search,
  Layers,
  Upload,
  Download,
  X,
  AlertCircle,
  GripVertical,
  ArrowUpDown,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useMasterServices, type MasterServiceItemInput } from '../../hooks/useMasterServices';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { MasterServiceItem, QuoteType } from '../../types/admin';

// ── Constants ──────────────────────────────────────────────────────────────

const PARTY_OPTIONS = ['Customer', 'Mover'] as const;
type Party = (typeof PARTY_OPTIONS)[number];

const CSV_TEMPLATE_HEADERS = 'service,code,party,note,order\n';
const CSV_TEMPLATE_FILENAME = 'master-services-template.csv';

// ── Helpers ────────────────────────────────────────────────────────────────

function downloadCsvTemplate() {
  const blob = new Blob([CSV_TEMPLATE_HEADERS], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = CSV_TEMPLATE_FILENAME;
  a.click();
  URL.revokeObjectURL(url);
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target?.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

// ── Label component ────────────────────────────────────────────────────────

function FieldLabel({
  htmlFor,
  children,
  optional,
}: {
  htmlFor: string;
  children: React.ReactNode;
  optional?: boolean;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-[11px] font-bold uppercase tracking-widest text-slate"
    >
      {children}
      {!optional && <span className="text-rose ml-0.5">*</span>}
      {optional && (
        <span className="text-slate font-normal normal-case tracking-normal ml-1">(optional)</span>
      )}
    </label>
  );
}

// ── Service Form ───────────────────────────────────────────────────────────
// Mobile: bottom sheet. md+: centered dialog.

interface ServiceFormProps {
  initial?: MasterServiceItem;
  onClose: () => void;
  onSave: (input: MasterServiceItemInput) => Promise<unknown>;
}

function ServiceForm({ initial, onClose, onSave }: ServiceFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [code, setCode] = useState(initial?.code ?? '');
  const [party, setParty] = useState<Party>(initial?.party ?? 'Customer');
  const [quoteType, setQuoteType] = useState<QuoteType>(initial?.quoteType ?? 'commercial');
  const [note, setNote] = useState(initial?.note ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError('Service name is required.'); return; }
    setError('');
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        code: code.trim() || undefined,
        party,
        note: note.trim() || undefined,
        quoteType,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save service.');
      setSaving(false);
    }
  }

  return (
    /* Mobile: slides up from bottom. md+: centered dialog. */
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[92vh] overflow-y-auto
                      md:rounded-2xl md:max-w-md md:w-full md:max-h-[80vh] md:pb-6">
        {/* Drag handle — mobile only */}
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">
            {initial ? 'Edit Service' : 'Add Service'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                       hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-rose/10 border border-rose/30">
              <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
              <p className="text-small text-rose">{error}</p>
            </div>
          )}

          {/* Service name */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="svc-name">Service</FieldLabel>
            <input
              id="svc-name"
              type="text"
              autoComplete="off"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Furniture Protection"
              className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          {/* Code */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="svc-code" optional>Code</FieldLabel>
            <input
              id="svc-code"
              type="text"
              autoComplete="off"
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder="e.g. A1 or MO2"
              className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          {/* Party */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="svc-party">Party</FieldLabel>
            <div className="flex bg-mist rounded-lg p-1 gap-1">
              {PARTY_OPTIONS.map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setParty(p)}
                  className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all duration-150
                    ${party === p
                      ? 'bg-white text-midnight shadow-brand-sm'
                      : 'text-slate hover:text-midnight'
                    }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Quote Type */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="svc-quotetype">Quote Type</FieldLabel>
            <div className="flex bg-mist rounded-lg p-1 gap-1">
              {(['commercial', 'residential'] as const).map(qt => (
                <button
                  key={qt}
                  type="button"
                  onClick={() => setQuoteType(qt)}
                  className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all duration-150
                    ${quoteType === qt
                      ? 'bg-white text-midnight shadow-brand-sm'
                      : 'text-slate hover:text-midnight'
                    }`}
                >
                  {qt === 'commercial' ? 'Commercial' : 'Residential'}
                </button>
              ))}
            </div>
          </div>

          {/* Note */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="svc-note" optional>Note</FieldLabel>
            <textarea
              id="svc-note"
              rows={2}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Optional context for estimators…"
              className="w-full px-3 py-2.5 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate resize-none
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            className="h-11 rounded-lg bg-plum text-white text-[13px] font-semibold
                       flex items-center justify-center gap-2
                       hover:bg-rose active:opacity-80 disabled:opacity-50
                       transition-colors duration-150 mt-1"
          >
            {saving ? 'Saving…' : initial ? 'Save Changes' : 'Add Service'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Party Badge ────────────────────────────────────────────────────────────

function PartyBadge({ party }: { party: Party }) {
  return (
    <span
      className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md
        ${party === 'Customer' ? 'bg-plum/10 text-plum' : 'bg-ember/10 text-ember'}`}
    >
      {party}
    </span>
  );
}

// ── Service Card ───────────────────────────────────────────────────────────

interface ServiceCardProps {
  service: MasterServiceItem;
  editingOrder: boolean;
  isDragOver: boolean;
  isDragging: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}

function ServiceCard({
  service,
  editingOrder,
  isDragOver,
  isDragging,
  onEdit,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: ServiceCardProps) {
  return (
    <div
      draggable={editingOrder}
      onDragStart={editingOrder ? onDragStart : undefined}
      onDragOver={editingOrder ? onDragOver : undefined}
      onDrop={editingOrder ? onDrop : undefined}
      onDragEnd={editingOrder ? onDragEnd : undefined}
      className={`bg-white border rounded-xl p-4 shadow-brand-sm select-none
        transition-all duration-100
        ${isDragOver ? 'border-plum border-2' : 'border-stone'}
        ${isDragging ? 'opacity-40' : 'opacity-100'}`}
    >
      <div className="flex items-center gap-3">
        {/* Drag handle — only visible in edit-order mode */}
        {editingOrder && (
          <div className="shrink-0 text-slate hover:text-midnight cursor-grab active:cursor-grabbing">
            <GripVertical size={18} />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-small text-midnight leading-snug">
              {service.code && <span className="text-plum font-bold mr-1.5">{service.code}</span>}
              {service.name}
            </p>
            <PartyBadge party={service.party} />
          </div>
          {service.note && (
            <p className="text-[13px] text-slate mt-1 leading-snug line-clamp-2">{service.note}</p>
          )}
        </div>

        {/* Actions — hidden while reordering */}
        {!editingOrder && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={onEdit}
              className="h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                         text-slate hover:border-plum hover:text-plum active:bg-mist
                         transition-colors duration-150"
              aria-label={`Edit ${service.name}`}
            >
              <Pencil size={14} />
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                         text-slate hover:border-rose hover:text-rose active:bg-mist
                         transition-colors duration-150"
              aria-label={`Delete ${service.name}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Bulk Result Banner ─────────────────────────────────────────────────────

interface BulkResultBannerProps {
  created: number;
  updated: number;
  onClose: () => void;
}

function BulkResultBanner({ created, updated, onClose }: BulkResultBannerProps) {
  const parts: string[] = [];
  if (created > 0) parts.push(`${created} added`);
  if (updated > 0) parts.push(`${updated} updated`);
  const summary = parts.length > 0 ? parts.join(', ') : 'no changes';

  return (
    <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-plum/8 border border-plum/20">
      <div className="flex-1 min-w-0">
        <p className="text-small font-semibold text-midnight">
          Bulk upload complete — {summary}
        </p>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="shrink-0 w-6 h-6 flex items-center justify-center text-slate
                   hover:text-midnight transition-colors"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}

// ── Bulk Upload Dropdown ───────────────────────────────────────────────────

interface BulkUploadButtonProps {
  onUpload: (csv: string) => Promise<void>;
  onUploadJson: (items: Record<string, unknown>[]) => Promise<void>;
  onDownloadJson: () => Promise<void>;
}

function BulkUploadButton({ onUpload, onUploadJson, onDownloadJson }: BulkUploadButtonProps) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const jsonInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  async function handleCsvChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setOpen(false);
    setUploading(true);
    try {
      const text = await readFileAsText(file);
      await onUpload(text);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function handleJsonChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setOpen(false);
    setUploading(true);
    try {
      const text = await readFileAsText(file);
      const parsed = JSON.parse(text);
      const items = Array.isArray(parsed) ? parsed : parsed.items ?? parsed.data ?? [];
      await onUploadJson(items);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        disabled={uploading}
        className="h-11 px-4 rounded-xl border border-stone bg-white
                   flex items-center gap-2
                   text-small font-semibold text-slate
                   hover:border-plum hover:text-plum active:bg-mist
                   disabled:opacity-50
                   transition-colors duration-150"
      >
        <Upload size={15} />
        {uploading ? 'Uploading…' : 'Bulk Upload'}
        <ChevronDown
          size={14}
          className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1.5 z-40 min-w-[200px]
                      bg-white border border-stone rounded-xl shadow-brand-md overflow-hidden"
        >
          <button
            type="button"
            onClick={() => { setOpen(false); csvInputRef.current?.click(); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-small text-midnight
                       hover:bg-mist active:bg-stone transition-colors text-left"
          >
            <Upload size={15} className="text-slate shrink-0" />
            Upload CSV File
          </button>
          <div className="h-px bg-stone mx-3" />
          <button
            type="button"
            onClick={() => { setOpen(false); jsonInputRef.current?.click(); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-small text-midnight
                       hover:bg-mist active:bg-stone transition-colors text-left"
          >
            <Upload size={15} className="text-slate shrink-0" />
            Upload JSON File
          </button>
          <div className="h-px bg-stone mx-3" />
          <button
            type="button"
            onClick={() => { setOpen(false); downloadCsvTemplate(); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-small text-midnight
                       hover:bg-mist active:bg-stone transition-colors text-left"
          >
            <Download size={15} className="text-slate shrink-0" />
            Download Template
          </button>
          <div className="h-px bg-stone mx-3" />
          <button
            type="button"
            onClick={() => { setOpen(false); onDownloadJson(); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-small text-midnight
                       hover:bg-mist active:bg-stone transition-colors text-left"
          >
            <Download size={15} className="text-slate shrink-0" />
            Download All Services
          </button>
        </div>
      )}

      <input
        ref={csvInputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={handleCsvChange}
      />
      <input
        ref={jsonInputRef}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={handleJsonChange}
      />
    </div>
  );
}

// ── Party column ───────────────────────────────────────────────────────────

interface PartyColumnProps {
  party: Party;
  services: MasterServiceItem[];
  editingOrder: boolean;
  draggedId: string | null;
  dragOverId: string | null;
  onEdit: (s: MasterServiceItem) => void;
  onDelete: (s: MasterServiceItem) => void;
  onDragStart: (e: React.DragEvent, id: string) => void;
  onDragOver: (e: React.DragEvent, id: string) => void;
  onDrop: (e: React.DragEvent, id: string) => void;
  onDragEnd: () => void;
}

function PartyColumn({
  party,
  services,
  editingOrder,
  draggedId,
  dragOverId,
  onEdit,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: PartyColumnProps) {
  const dotColor = party === 'Customer' ? 'bg-plum' : 'bg-ember';

  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2 flex items-center gap-1.5">
        <span className={`inline-block w-2 h-2 rounded-full ${dotColor}`} />
        {party}
        <span className="font-normal normal-case tracking-normal">({services.length})</span>
      </p>
      {services.length === 0 ? (
        <p className="text-[13px] text-slate py-4">None yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {services.map(service => (
            <li key={service.id}>
              <ServiceCard
                service={service}
                editingOrder={editingOrder}
                isDragOver={dragOverId === service.id}
                isDragging={draggedId === service.id}
                onEdit={() => onEdit(service)}
                onDelete={() => onDelete(service)}
                onDragStart={e => onDragStart(e, service.id)}
                onDragOver={e => onDragOver(e, service.id)}
                onDrop={e => onDrop(e, service.id)}
                onDragEnd={onDragEnd}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Main Page Content ──────────────────────────────────────────────────────

function MasterServicesContent() {
  const navigate = useNavigate();
  const { items, loading, error, createItem, updateItem, deleteItem, reorderItems, bulkUpload, downloadJson, bulkUploadJson } =
    useMasterServices();

  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<QuoteType>('commercial');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<MasterServiceItem | null>(null);
  const [deletingItem, setDeletingItem] = useState<MasterServiceItem | null>(null);
  const [bulkResult, setBulkResult] = useState<{ created: number; updated: number } | null>(null);
  const [bulkError, setBulkError] = useState('');

  // Edit-order mode
  const [editingOrder, setEditingOrder] = useState(false);
  const [orderedItems, setOrderedItems] = useState<MasterServiceItem[]>([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  function enterEditOrder() {
    setOrderedItems([...items]);
    setEditingOrder(true);
  }

  function cancelEditOrder() {
    setEditingOrder(false);
    setOrderedItems([]);
    setDraggedId(null);
    setDragOverId(null);
  }

  async function saveOrder() {
    setSavingOrder(true);
    try {
      await reorderItems(orderedItems.map(s => s.id));
      setEditingOrder(false);
      setOrderedItems([]);
    } finally {
      setSavingOrder(false);
    }
  }

  function handleDragStart(e: React.DragEvent, id: string) {
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedId(id);
  }

  function handleDragOver(e: React.DragEvent, id: string) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== draggedId) setDragOverId(id);
  }

  function handleDrop(e: React.DragEvent, targetId: string) {
    e.preventDefault();
    const sourceId = draggedId ?? e.dataTransfer.getData('text/plain');
    if (!sourceId || sourceId === targetId) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }
    setOrderedItems(prev => {
      const source = prev.find(s => s.id === sourceId);
      const target = prev.find(s => s.id === targetId);
      // Reordering only within the same party group
      if (!source || !target || source.party !== target.party) return prev;
      const result = [...prev];
      const srcIdx = result.findIndex(s => s.id === sourceId);
      const tgtIdx = result.findIndex(s => s.id === targetId);
      result.splice(srcIdx, 1);
      result.splice(tgtIdx, 0, source);
      return result;
    });
    setDraggedId(null);
    setDragOverId(null);
  }

  function handleDragEnd() {
    setDraggedId(null);
    setDragOverId(null);
  }

  async function handleBulkUpload(csv: string) {
    setBulkError('');
    setBulkResult(null);
    try {
      const result = await bulkUpload(csv);
      setBulkResult(result);
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Bulk upload failed.');
    }
  }

  async function handleBulkUploadJson(jsonItems: Record<string, unknown>[]) {
    setBulkError('');
    setBulkResult(null);
    try {
      const result = await bulkUploadJson(jsonItems);
      setBulkResult(result);
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'JSON upload failed.');
    }
  }

  async function handleDownloadJson() {
    try {
      await downloadJson();
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Download failed.');
    }
  }

  // Filter by view mode first
  const viewItems = items.filter(s =>
    viewMode === 'commercial'
      ? (s.quoteType ?? 'commercial') === 'commercial'
      : s.quoteType === 'residential',
  );

  const displayItems = editingOrder ? orderedItems : viewItems;
  const filtered = editingOrder
    ? orderedItems.filter(s =>
        viewMode === 'commercial'
          ? (s.quoteType ?? 'commercial') === 'commercial'
          : s.quoteType === 'residential',
      )
    : viewItems.filter(
        s =>
          s.name.toLowerCase().includes(search.toLowerCase()) ||
          s.code?.toLowerCase().includes(search.toLowerCase()) ||
          s.party.toLowerCase().includes(search.toLowerCase()) ||
          s.note?.toLowerCase().includes(search.toLowerCase()),
      );

  const customerItems = filtered.filter(s => s.party === 'Customer');
  const moverItems = filtered.filter(s => s.party === 'Mover');

  const sharedColumnProps = {
    editingOrder,
    draggedId,
    dragOverId,
    onEdit: setEditingItem,
    onDelete: setDeletingItem,
    onDragStart: handleDragStart,
    onDragOver: handleDragOver,
    onDrop: handleDrop,
    onDragEnd: handleDragEnd,
  };

  return (
    <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-5xl md:mx-auto">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate('/admin/platform-tools')}
        className="flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ChevronLeft size={16} />
        Platform Admin Tools
      </button>

      <h1 className="font-display text-h1 text-midnight mb-1">Master Services</h1>
      <p className="text-[13px] text-slate mb-5 leading-snug">
        Platform-wide list of services. Defines what's available to mark in scope for each job.
      </p>

      {/* Banners */}
      {bulkResult && (
        <div className="mb-4">
          <BulkResultBanner {...bulkResult} onClose={() => setBulkResult(null)} />
        </div>
      )}
      {bulkError && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-small text-rose">{bulkError}</p>
          </div>
          <button
            type="button"
            onClick={() => setBulkError('')}
            className="shrink-0 w-6 h-6 flex items-center justify-center text-rose"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Commercial / Residential toggle */}
      <div className="flex bg-mist rounded-lg p-1 gap-1 mb-4">
        {(['commercial', 'residential'] as const).map(qt => (
          <button
            key={qt}
            type="button"
            onClick={() => { setViewMode(qt); setSearch(''); }}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all duration-150
              ${viewMode === qt
                ? 'bg-white text-midnight shadow-brand-sm'
                : 'text-slate hover:text-midnight'
              }`}
          >
            {qt === 'commercial' ? 'Commercial' : 'Residential'}
          </button>
        ))}
      </div>

      {/* Toolbar — normal mode */}
      {!editingOrder && (
        <div className="flex items-center gap-2 mb-5">
          <div className="relative flex-1">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate pointer-events-none"
            />
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search services…"
              className="w-full h-11 pl-9 pr-3 rounded-xl border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>
          <button
            type="button"
            onClick={enterEditOrder}
            disabled={items.length < 2}
            className="shrink-0 h-11 w-11 rounded-xl border border-stone bg-white
                       flex items-center justify-center
                       text-slate hover:border-plum hover:text-plum active:bg-mist
                       disabled:opacity-40 disabled:cursor-not-allowed
                       transition-colors duration-150"
            aria-label="Edit order"
            title="Edit order"
          >
            <ArrowUpDown size={16} />
          </button>
          <BulkUploadButton onUpload={handleBulkUpload} onUploadJson={handleBulkUploadJson} onDownloadJson={handleDownloadJson} />
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="shrink-0 h-11 w-11 rounded-xl bg-plum text-white
                       flex items-center justify-center
                       hover:bg-rose active:opacity-80
                       transition-colors duration-150"
            aria-label="Add service"
          >
            <Plus size={20} />
          </button>
        </div>
      )}

      {/* Toolbar — edit order mode */}
      {editingOrder && (
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <p className="flex-1 text-small text-slate">
            Drag <span className="font-semibold text-midnight">within</span> each column to reorder.
          </p>
          <button
            type="button"
            onClick={cancelEditOrder}
            disabled={savingOrder}
            className="h-11 px-4 rounded-xl border border-stone bg-white
                       text-small font-semibold text-slate
                       hover:border-rose hover:text-rose active:bg-mist
                       disabled:opacity-50
                       transition-colors duration-150"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={saveOrder}
            disabled={savingOrder}
            className="h-11 px-4 rounded-xl bg-plum text-white
                       text-small font-semibold
                       hover:bg-rose active:opacity-80 disabled:opacity-50
                       transition-colors duration-150"
          >
            {savingOrder ? 'Saving…' : 'Save Order'}
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">Failed to load services</p>
          <p className="text-small text-slate">{error}</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && viewItems.length === 0 && (
        <div className="flex flex-col items-center py-16 gap-3 text-center">
          <div className="w-14 h-14 rounded-full bg-mist flex items-center justify-center">
            <Layers size={24} className="text-slate" />
          </div>
          <p className="text-small text-slate">No {viewMode} services yet. Add one or bulk upload a CSV.</p>
        </div>
      )}

      {/* No search results */}
      {!loading && !error && !editingOrder && viewItems.length > 0 && filtered.length === 0 && (
        <div className="flex flex-col items-center py-12 gap-2 text-center">
          <p className="text-small text-slate">No services match your search.</p>
        </div>
      )}

      {/* Service columns
          Mobile:  stacked Customer then Mover sections.
          md+:     side-by-side columns — Customer left, Mover right.
          Each column renders its own header, count, and card list.
      */}
      {!loading && !error && (editingOrder ? displayItems.length > 0 : filtered.length > 0) && (
        <>
          {!editingOrder && (
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">
              {filtered.length} service{filtered.length !== 1 ? 's' : ''}
              {search && ` matching "${search}"`}
            </p>
          )}

          <div className="flex flex-col gap-6 md:flex-row md:gap-8 md:items-start">
            <div className="md:flex-1 min-w-0">
              <PartyColumn
                party="Customer"
                services={customerItems}
                {...sharedColumnProps}
              />
            </div>
            <div className="md:flex-1 min-w-0">
              <PartyColumn
                party="Mover"
                services={moverItems}
                {...sharedColumnProps}
              />
            </div>
          </div>
        </>
      )}

      {/* Add form */}
      {showAddForm && (
        <ServiceForm
          onClose={() => setShowAddForm(false)}
          onSave={createItem}
        />
      )}

      {/* Edit form */}
      {editingItem && (
        <ServiceForm
          initial={editingItem}
          onClose={() => setEditingItem(null)}
          onSave={input => updateItem(editingItem.id, input)}
        />
      )}

      {/* Delete confirm */}
      <ConfirmModal
        isOpen={!!deletingItem}
        title="Delete Service"
        message={`Are you sure you want to delete "${deletingItem?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (deletingItem) deleteItem(deletingItem.id);
          setDeletingItem(null);
        }}
        onClose={() => setDeletingItem(null)}
      />
    </div>
  );
}

// ── Page (guards IOMI admin access) ───────────────────────────────────────

export function MasterServicesPage() {
  const { currentUser, loading } = useUserProfile();

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!currentUser?.isPlatformAdmin && IS_FIREBASE_CONFIGURED) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-3 text-center">
        <p className="text-small text-slate">Platform Admin access required.</p>
      </div>
    );
  }

  return <MasterServicesContent />;
}
